// Instrument-level "mkt" pill resolution for the holdings panel.
//
// While the US regular session is OPEN the pill is the live intraday day
// change: current price vs T-1 close (legacy behaviour, with the 5%
// staleness fallback from the 2026-05-12 DDOG incident).
//
// While the session is CLOSED the pill shows the COMPLETED session's
// change — session_close vs session_prev_close, TradingView convention —
// because the "current" price outside RTH degrades to a beta-scaled
// NAS100 proxy that is structurally blind to idiosyncratic moves
// (2026-07-13: GH fell -0.96% on the day, its proxy pill showed -0.21%).
// The proxy/after-hours drift since that close is returned separately so
// the UI can render it as a secondary figure.

const T1_STALENESS_THRESHOLD = 0.05 // 5% divergence ⇒ t1 suspect
const T1_CORROBORATION_TOLERANCE = 0.001 // 10bp — t1 vs session_prev_close agreement
const AH_DISPLAY_THRESHOLD = 0.0001 // 1bp — below this the drift is noise

export interface MarketReturnInputs {
  /** Freshest price the panel has: live mid ?? proxy ?? after-hours close. */
  cur: number | null
  /** prev_close_t1 — T-1 close from the AV daily pipeline CSVs. */
  t1Close: number | null
  /** prev_close — the signal engine's working close (refreshed every cycle). */
  prevClose: number | null
  /** Last completed session close (payload session_close). */
  sessionClose: number | null
  /** The session close before that (payload session_prev_close). */
  sessionPrevClose: number | null
  /** Payload us_rth_open; null/undefined = old publisher → legacy path. */
  rthOpen: boolean | null | undefined
}

export interface MarketReturnResult {
  marketReturn: number | null
  /** Drift since the last session close; only set when the market is closed
   *  and the drift is at least 1bp. */
  afterHoursReturn: number | null
}

function legacyReturn(
  cur: number | null,
  t1Close: number | null,
  prevClose: number | null,
  sessionPrevClose: number | null,
): number | null {
  if (cur == null) return null
  if (t1Close != null && t1Close > 0 && prevClose != null && prevClose > 0) {
    // The 5% t1-vs-working-close divergence guard (DDOG 2026-05-12) cannot
    // tell a stale t1 from a GENUINE >5% overnight move already embedded in
    // the strike-anchored working close (TSLA earnings 2026-07-23: the pill
    // silently re-anchored to prev_close and showed -8% instead of -14%).
    // Discriminate with the independent Databento session close: when
    // session_prev_close corroborates t1, t1 is healthy — keep it as the
    // day-change anchor no matter how large the overnight gap.
    const corroborated =
      sessionPrevClose != null && sessionPrevClose > 0 &&
      Math.abs(t1Close / sessionPrevClose - 1) <= T1_CORROBORATION_TOLERANCE
    const drift = Math.abs(t1Close / prevClose - 1)
    if (drift > T1_STALENESS_THRESHOLD && !corroborated) return cur / prevClose - 1
    return cur / t1Close - 1
  }
  if (t1Close != null && t1Close > 0) return cur / t1Close - 1
  if (prevClose != null && prevClose > 0) return cur / prevClose - 1
  return null
}

export function resolveMarketReturn(i: MarketReturnInputs): MarketReturnResult {
  const closedWithSessionData =
    i.rthOpen === false &&
    i.sessionClose != null && i.sessionClose > 0 &&
    i.sessionPrevClose != null && i.sessionPrevClose > 0

  if (closedWithSessionData) {
    const marketReturn = i.sessionClose! / i.sessionPrevClose! - 1
    let afterHoursReturn: number | null = null
    if (i.cur != null) {
      const drift = i.cur / i.sessionClose! - 1
      if (Math.abs(drift) >= AH_DISPLAY_THRESHOLD) afterHoursReturn = drift
    }
    return { marketReturn, afterHoursReturn }
  }

  return {
    marketReturn: legacyReturn(i.cur, i.t1Close, i.prevClose, i.sessionPrevClose),
    afterHoursReturn: null,
  }
}
